import { FormError } from '@/components/ui/FormError';
import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { api, ApiError } from '@/shared/api/client';
import { useAuthStore, AuthUser, CompanyInfo } from '@/shared/store/authStore';
import { PageHeader } from '@/components/ui/PageHeader';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { FormField } from '@/components/ui/FormField';

type PageProps = { embedded?: boolean; onDone?: () => void };
type MeResp = { user: AuthUser; company: CompanyInfo };

const MIN = 12;

export function ChangePasswordPage({ embedded = false, onDone }: PageProps = {}) {
  const nav = useNavigate();
  const user = useAuthStore((s) => s.user);
  const setSession = useAuthStore((s) => s.setSession);
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [ok, setOk] = useState(false);
  const forced = !!user?.must_change_password;

  const change = useMutation({
    mutationFn: () => api.post('/auth/change-password', { current_password: current, new_password: next }),
    onSuccess: async () => {
      setOk(true); setErr(null); setCurrent(''); setNext(''); setConfirm('');
      // The server cleared must_change_password; refresh the session so the
      // route guard lets the user back into the app.
      try {
        const me = await api.get<MeResp>('/auth/me');
        setSession(me.user, me.company);
      } catch { /* the guard falls back to /login */ }
      setTimeout(() => {
        if (onDone) onDone();
        else nav('/');
      }, 1500);
    },
    onError: (e) => {
      setOk(false);
      if (e instanceof ApiError) {
        if (e.code === 'invalid_current_password') setErr('Current password is incorrect.');
        else setErr(e.message);
      } else setErr('Failed to change password');
    },
  });

  function submit() {
    setErr(null); setOk(false);
    if (!current || !next) { setErr('Enter your current and new password.'); return; }
    if (next.length < MIN) { setErr(`New password must be at least ${MIN} characters.`); return; }
    if (next !== confirm) { setErr("New password and confirmation don't match."); return; }
    if (next === current) { setErr('New password must differ from current.'); return; }
    change.mutate();
  }

  const form = (
    <div className="space-y-3">
      {forced && (
        <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-body text-amber-900">
          Your administrator issued a temporary password. Choose a new one to continue.
        </div>
      )}
      <FormField label="Current password" required>
        <Input type="password" value={current} onChange={(e) => setCurrent(e.target.value)} autoComplete="current-password" />
      </FormField>
      <FormField label="New password" required hint={`At least ${MIN} characters; not a common word, a date, or your email. A long passphrase works best.`}>
        <Input type="password" value={next} onChange={(e) => setNext(e.target.value)} autoComplete="new-password" />
      </FormField>
      <FormField label="Confirm new password" required>
        <Input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} autoComplete="new-password" />
      </FormField>
      <FormError className="text-[0.75rem] text-danger">{err}</FormError>
      {ok && <div className="text-[0.75rem] text-emerald-700">Password updated. {onDone ? '' : 'Redirecting...'}</div>}
      <Button onClick={submit} loading={change.isPending} className="w-full">Update password</Button>
    </div>
  );

  if (embedded) return form;

  return (
    <div>
      <PageHeader title="Change password" description="Updating your password also signs out every other active session." />
      <Card className="max-w-lg">{form}</Card>
    </div>
  );
}
